import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, KeyRound, ShieldCheck } from "lucide-react";

/**
 * Forced password-change screen shown to any user whose admin has reset
 * their password. They cannot navigate anywhere else until they set a
 * password of their own.
 */
export default function ChangePasswordRequired() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) return toast.error("New password must be at least 8 characters");
    if (newPw !== confirmPw) return toast.error("Passwords do not match");
    if (newPw === currentPw) return toast.error("New password must be different from the temporary one");
    setLoading(true);
    try {
      await api.post("/auth/me/password", { current_password: currentPw, new_password: newPw });
      toast.success("Password updated — welcome!");
      // Refresh user object so must_change_password is cleared client-side
      if (refresh) await refresh();
      // Route based on role
      const dest = user?.role === "family" ? "/family" : user?.role === "sitter" ? "/sitter" : user?.role === "admin" ? "/admin" : "/";
      navigate(dest, { replace: true });
    } catch (err) { toast.error(extractError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="change-password-required-page">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-14">
        <div className="paper-card p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 text-ochre-700">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold">Security check</span>
          </div>
          <h1 className="mt-2 font-heading font-bold text-sage-900 text-2xl flex items-center gap-2">
            <KeyRound className="w-6 h-6 text-sage-600" /> Set your new password
          </h1>
          <p className="mt-2 text-sm text-stone2-600 leading-relaxed">
            Your account was reset by an administrator. For your security, please choose a password only you know before continuing.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="field-label" htmlFor="cur">Temporary password (from your admin)</label>
              <input id="cur" type="password" required autoFocus
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                data-testid="cpw-current"
                className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500" />
            </div>
            <div>
              <label className="field-label" htmlFor="new">New password <span className="text-stone2-400 font-normal">(min 8 chars, 1 letter + 1 number)</span></label>
              <input id="new" type="password" required minLength={8}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                data-testid="cpw-new"
                className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500" />
            </div>
            <div>
              <label className="field-label" htmlFor="conf">Confirm new password</label>
              <input id="conf" type="password" required minLength={8}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                data-testid="cpw-confirm"
                className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500" />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="cpw-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              Set new password & continue
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
